import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  PlusOutlined,
  ThunderboltOutlined,
  ShopOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { dataEntryAPI, stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;

interface EVChargingPrice {
  _id: string;
  station: {
    _id: string;
    name: string;
  };
  chargerType: string;
  power: number;
  pricingType: string;
  price: number;
  unit: string;
  minCharge: number;
  effectiveDate: string;
  isActive: boolean;
}

const EVChargingPrices: React.FC = () => {
  const [prices, setPrices] = useState<EVChargingPrice[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedStation, setSelectedStation] = useState<string>('all');
  const [isMobile, setIsMobile] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchPrices();
  }, [selectedStation]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchPrices = async () => {
    setLoading(true);
    try {
      const stationId = selectedStation === 'all' ? undefined : selectedStation;
      const response = await dataEntryAPI.getEVChargingPrices(stationId);
      setPrices(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fiyatlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPrice = () => {
    form.resetFields();
    form.setFieldsValue({
      effectiveDate: dayjs(),
      unit: 'kwh',
      pricingType: 'per_kwh',
      minCharge: 0
    });
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        stationId: values.station,
        chargerType: values.chargerType,
        power: values.power,
        pricingType: values.pricingType,
        price: values.price,
        unit: values.unit,
        minCharge: values.minCharge || 0,
        effectiveDate: values.effectiveDate?.toISOString()
      };

      await dataEntryAPI.addEVChargingPrice(data);
      message.success('EV şarj fiyatı başarıyla eklendi');
      setModalVisible(false);
      form.resetFields();
      fetchPrices();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const chargerTypeLabels: any = {
    AC: 'AC (Alternatif Akım)',
    DC: 'DC (Doğru Akım)',
    'Tesla Supercharger': 'Tesla Supercharger',
    CHAdeMO: 'CHAdeMO',
    CCS: 'CCS'
  };

  const pricingTypeLabels: any = {
    per_kwh: 'kWh Başına',
    per_minute: 'Dakika Başına',
    flat_rate: 'Sabit Ücret'
  };

  const columns = [
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station',
    },
    {
      title: 'Şarj Tipi',
      dataIndex: 'chargerType',
      key: 'chargerType',
      render: (type: string) => (
        <Tag color="purple">{chargerTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: 'Güç (kW)',
      dataIndex: 'power',
      key: 'power',
      render: (power: number) => `${power} kW`,
    },
    {
      title: 'Fiyatlandırma',
      dataIndex: 'pricingType',
      key: 'pricingType',
      render: (type: string) => pricingTypeLabels[type] || type,
    },
    {
      title: 'Fiyat',
      dataIndex: 'price',
      key: 'price',
      render: (price: number, record: EVChargingPrice) => (
        <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#52c41a' }}>
          {price.toLocaleString('tr-TR')} ₺ / {record.unit}
        </span>
      ),
    },
    {
      title: 'Min. Ücret',
      dataIndex: 'minCharge',
      key: 'minCharge',
      render: (charge: number) => charge > 0 ? `${charge.toLocaleString('tr-TR')} ₺` : '-',
    },
    {
      title: 'Geçerlilik Tarihi',
      dataIndex: 'effectiveDate',
      key: 'effectiveDate',
      render: (date: string) => dayjs(date).format('DD MMMM YYYY'),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }} gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: '24px' }}>
            <ThunderboltOutlined /> EV Şarj Fiyat Yönetimi
          </Title>
        </Col>
        <Col xs={24} sm={24} md={12}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Select
              value={selectedStation}
              onChange={setSelectedStation}
              style={{ width: '100%', maxWidth: 200 }}
              placeholder="İstasyon seçin"
            >
              <Option value="all">Tüm İstasyonlar</Option>
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddPrice} block={isMobile}>
              Yeni Fiyat Ekle
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Özet İstatistikler */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Aktif Fiyat"
              value={prices.filter(p => p.isActive).length}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Ortalama Fiyat"
              value={prices.filter(p => p.isActive).length > 0
                ? prices.filter(p => p.isActive).reduce((sum, p) => sum + p.price, 0) / prices.filter(p => p.isActive).length
                : 0}
              prefix={<DollarOutlined />}
              suffix="₺"
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="İstasyon Sayısı"
              value={new Set(prices.map(p => p.station._id)).size}
              prefix={<ShopOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Şarj Tipi Sayısı"
              value={new Set(prices.map(p => p.chargerType)).size}
              prefix={<ThunderboltOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={prices}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>

      <Modal
        title="Yeni EV Şarj Fiyatı Ekle"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="station"
            label="İstasyon"
            rules={[{ required: true, message: 'İstasyon seçiniz' }]}
          >
            <Select placeholder="İstasyon seçin">
              {stations.map((station: any) => (
                <Option key={station._id} value={station._id}>
                  {station.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="chargerType"
                label="Şarj Tipi"
                rules={[{ required: true, message: 'Şarj tipi seçiniz' }]}
              >
                <Select placeholder="Şarj tipi">
                  <Option value="AC">AC (Alternatif Akım)</Option>
                  <Option value="DC">DC (Doğru Akım)</Option>
                  <Option value="Tesla Supercharger">Tesla Supercharger</Option>
                  <Option value="CHAdeMO">CHAdeMO</Option>
                  <Option value="CCS">CCS</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="power"
                label="Güç (kW)"
                rules={[{ required: true, message: 'Güç değeri giriniz' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  placeholder="Güç"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="pricingType"
                label="Fiyatlandırma Tipi"
                rules={[{ required: true, message: 'Fiyatlandırma tipi seçiniz' }]}
              >
                <Select>
                  <Option value="per_kwh">kWh Başına</Option>
                  <Option value="per_minute">Dakika Başına</Option>
                  <Option value="flat_rate">Sabit Ücret</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="unit"
                label="Birim"
                rules={[{ required: true, message: 'Birim seçiniz' }]}
              >
                <Select>
                  <Option value="kwh">kWh</Option>
                  <Option value="minute">Dakika</Option>
                  <Option value="session">Oturum</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="price"
                label="Fiyat (₺)"
                rules={[{ required: true, message: 'Fiyat giriniz' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="Fiyat"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="minCharge"
                label="Minimum Ücret (₺)"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  placeholder="Min. ücret"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="effectiveDate"
            label="Geçerlilik Tarihi"
            rules={[{ required: true, message: 'Tarih seçiniz' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Ekle
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EVChargingPrices;

